// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { DataTablePresentationProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableBody, DataTableCell, DataTableRow, Typography } from "@insite/mobius/index";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import loadProductBoxes from "@steiner/App/Handlers/ProductDetails/LoadProductBoxes";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import DataTable from "@steiner/MobiusOverrides/DataTable";
import ProductAddToCartButton from "@steiner/Overrides/Components/ProductAddToCartButton";
import * as React from "react";
import { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState, ownProps: HasProductContext) => ({
    boxes: state.xProductDetails.byId[ownProps.productContext.product.id]?.boxes,
});

const mapDispatchToProps = {
    loadProductBoxes,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProductContext;

export interface ProductBoxesStyle {
    datatable?: DataTablePresentationProps;
    qtyInput?: TextFieldPresentationProps;
    addToCartButton?: ButtonPresentationProps;
    qtyCell?: DataTableCellProps;
    addToCartCell?: DataTableCellProps;
    labelCell?: DataTableCellProps;
}

export const productBoxesStyle: ProductBoxesStyle = {};

const styles = productBoxesStyle;

const ProductBoxes: FC<Props> = ({ productContext, boxes, loadProductBoxes }) => {
    const [quantities, setQuantities] = useState<{ [key: string]: number }>({});

    useEffect(() => {
        if (!boxes?.length) {
            loadProductBoxes({
                productId: productContext.product.id,
            });
        }
    }, [productContext.product.id]);

    useEffect(() => {
        if (boxes && boxes.length > 0) {
            const initialQuantities = boxes.reduce((acc, box) => {
                acc[box.boxDescription] = box.qtyOrdered || 1;
                return acc;
            }, {} as { [key: string]: number });
            setQuantities(initialQuantities);
        }
    }, [boxes]);

    if (productContext.product.isVariantParent || productContext.product.isDiscontinued || !boxes?.length) {
        return null;
    }

    const qtyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, boxDescription: string) => {
        const newQty = Number(event.target.value);
        setQuantities(prev => ({
            ...prev,
            [boxDescription]: newQty,
        }));
    };

    return (
        <ProductContext.Provider value={productContext}>
            <DataTable
                {...styles.datatable}
                data-test-selector="product_boxes"
                useMobileLayout={false}
                usePrintLayout={false}
            >
                <DataTableBody>
                    {boxes?.map(box => (
                        <DataTableRow key={box.boxDescription}>
                            <DataTableCell {...styles.labelCell}>
                                {translate("Order {0} (qty {1}) x", box.boxDescription, box.boxQty.toString())}
                            </DataTableCell>
                            <DataTableCell {...styles.qtyCell}>
                                <TextField
                                    type="qty"
                                    value={quantities[box.boxDescription]}
                                    onChange={event => qtyChangeHandler(event, box.boxDescription)}
                                    {...styles.qtyInput}
                                />
                            </DataTableCell>
                            <DataTableCell {...styles.labelCell}>
                                <Typography weight="bold">
                                    {translate(
                                        "= {0}",
                                        box.boxQty == null || quantities?.[box.boxDescription] == null
                                            ? "-"
                                            : ((quantities[box.boxDescription] ?? 0) * (box.boxQty ?? 0)).toString(),
                                    )}
                                </Typography>
                            </DataTableCell>
                            <DataTableCell {...styles.addToCartCell}>
                                <ProductAddToCartButton
                                    disabled={!quantities[box.boxDescription]}
                                    displayAsIcon={true}
                                    extendedStyles={styles.addToCartButton}
                                    labelOverride=""
                                    overrideQty={quantities[box.boxDescription] * box.boxQty}
                                />
                            </DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        </ProductContext.Provider>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductBoxes));
